import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  EmptyState,
  failedEmptyState,
  useLoadPhase,
  useToast,
} from "../../../components/ui";
import { billingCheckoutPath } from "../../../routes/paths";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import BillingHistoryCard from "./BillingHistoryCard";
import CancelPlanCard from "./CancelPlanCard";
import CancelSubscriptionModal from "./CancelSubscriptionModal";
import PlanCard from "./PlanCard";
import PlansSkeleton from "./PlansSkeleton";
import RefundProgressBanner from "./RefundProgressBanner";
import RefundedPlanCard from "./RefundedPlanCard";
import SubscriptionStatusCard from "./SubscriptionStatusCard";
import TrialPlanCard from "./TrialPlanCard";
import TrialUpgradeCard from "./TrialUpgradeCard";
import { CYCLE_LABELS, PRODUCT_NAME } from "../data/subscriptionPlans";
import { trialCountdown } from "../trialCountdown";
import { useBillingHistory } from "../hooks/useBillingHistory";
import { useCurrentSubscription } from "../hooks/useCurrentSubscription";
import { usePlans } from "../hooks/usePlans";
import { useRefundOffer } from "../hooks/useRefundOffer";
import {
  fetchInvoiceDocument,
  saveInvoicePdf,
} from "../services/subscriptionsService";
import type { BillingCycleId, SubscriptionPlan } from "../types/billing.types";
import type { SubscriptionInvoice } from "../types/subscriptions.types";

type BillingView = "plans" | "details";

/** What a screen needs to call a plan by name. Less than a whole
 *  `SubscriptionPlan`, deliberately — see `planName`. */
interface PlanName {
  productName: string;
  cycleLabel: string;
}

const VIEWS: Array<{ id: BillingView; label: string }> = [
  { id: "plans", label: "Plans" },
  { id: "details", label: "Plan details" },
];

/** How often a refund still settling is asked about again — see `settling`,
 *  which is the only thing that turns the poll on. */
const REFUND_POLL_MS = 2 * 60 * 1000;

/**
 * Billing: what the workspace is paying for, or — for one that is not paying
 * yet — what it could.
 *
 * A section rather than a screen. It used to be `/billing`, opened from its own
 * rail item; it is the Settings screen's Billings tab now, which is why it
 * draws no page header and no page padding of its own — `SettingsPage` owns
 * both. `/billing` still redirects here, and the checkout under it is still a
 * screen: see `BILLING_SETTINGS_PATH`.
 *
 * ## Two views, and only for a workspace with a plan
 *
 * A workspace with nothing running sees the price list, full stop. There is
 * nothing else it could be shown: the Plan details screen is a subscription, its
 * invoices and the ways out of it, and a workspace with none of those has
 * nothing to administer. It used to be shown a made-up one — there was no
 * endpoint to draw a real one from — and that has gone with the endpoints
 * landing.
 *
 * A subscriber gets both, and opens on their own plan. The price list stays
 * within reach because moving from the month to the year is exactly what it is
 * still for; the cycle they are on is marked and cannot be bought again — see
 * `activeCycle`.
 *
 * So does a workspace on the free trial, which is the *other* way the product
 * is open and has its own pair of cards on the details view — see `trialing`,
 * `TrialPlanCard` and `TrialUpgradeCard`. It used to fall through to the price
 * list with the never-subscribed, which showed somebody with a fortnight of
 * complete access no sign of what they were on and no sign of how long they had
 * it. Its Upgrade button is the price list, so the switch is what a trial moves
 * between as much as a subscriber does.
 *
 * It is a pill group rather than the tab bar it was, because the tab bar above
 * it is the Settings screen's now and two of those in a column read as one
 * confused control.
 */
const BillingSection: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const getToken = useTokenGetter();

  const { subscription, trial, accessSource, cancel, reload } =
    useCurrentSubscription();
  const history = useBillingHistory();
  const priceList = usePlans();
  // Read for one thing now: whether there is a refund on the record and what
  // has become of it. The offer half of the answer — `eligible`, the window,
  // the reasons — is no longer drawn anywhere, since a refund is not asked for
  // from this screen. See `useRefundOffer`.
  const refundOffer = useRefundOffer();

  const [cancelling, setCancelling] = useState(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  // Which invoice's PDF is being fetched, so one row spins rather than the
  // whole table going busy for a download that belongs to one line of it.
  const [downloading, setDownloading] = useState<string | null>(null);

  const historyPhase = useLoadPhase(history.status === "loading");
  const plansPhase = useLoadPhase(priceList.status === "loading");

  /**
   * What to call the plan on a given cycle.
   *
   * Off the fetched list where it has arrived, so a plan is named the way the
   * seller names it. Off this app's own words where it has not — because the
   * details screen is about a subscription somebody already has, and their plan
   * should not go unnamed, their invoices unreachable and their cancel button
   * unreachable with it, because a price list they are not even being shown
   * failed to load. The price list itself gets no such fallback: a figure is
   * not something to guess at. See `plansService`.
   */
  const planName = (cycle: BillingCycleId): PlanName =>
    priceList.plans.find((plan) => plan.id === cycle) ?? {
      productName: PRODUCT_NAME,
      cycleLabel: CYCLE_LABELS[cycle],
    };

  /**
   * Whether this workspace is a subscriber rather than a shopper.
   *
   * Every state except the ones that are over — a plan whose payment has
   * failed still has a plan, and the screen that says so is the one with the
   * button that fixes it. `CREATED` counts too: a subscription made but never
   * authorised is a checkout somebody walked away from, and showing them their
   * own half-finished purchase is more use than showing them the price list
   * again.
   */
  /**
   * The cycle the workspace is actually running on, which is the one card that
   * says "Active" and the one that cannot be bought.
   *
   * Narrower than `subscribed` below, deliberately. That one asks which screen
   * to open and counts everything with a plan in it — an unauthorised checkout,
   * a refund on its way back — because all of those have something to
   * administer. This asks whether the plan is what is unlocking the product
   * right now, which is the server's `access_source` to say, and it is the
   * only claim a badge on the price list can honestly make: a `CREATED`
   * subscription has never been charged and a `HALTED` one has stopped, the
   * server answers neither with `SUBSCRIPTION`, and telling either that this
   * is their active plan while barring the button that would fix it is the
   * worst of both. A refund closes it the same way, however the record still
   * reads — and buying it again is exactly what the refunded card offers.
   */
  const activeCycle =
    subscription && accessSource === "SUBSCRIPTION" ? subscription.cycle : null;

  /**
   * What became of the money, where a refund was asked for at all.
   *
   * Off `GET /billing/refund/` and not off the subscription: the billing API's
   * subscription record has no refund on it, so this is the only place the
   * screen can learn there is one. It is what puts the details view into the
   * refund states.
   */
  const refunded = refundOffer.offer.request;

  const subscribed =
    !!subscription &&
    // A refunded plan is `CANCELLED` and still belongs on this screen: what
    // became of the money, and the way back, are both here — and the price
    // list is not an answer to "where is my refund".
    (!!refunded ||
      !["CANCELLED", "COMPLETED", "EXPIRED"].includes(subscription.status));

  /**
   * How much of the free trial is left, for a workspace on one.
   *
   * `null` for everybody else — a workspace that never took a trial, and one
   * whose trial the server calls finished. `active` is the backend's own flag
   * and not a comparison of dates against this machine's clock; see
   * `trialCountdown`.
   */
  const countdown = trialCountdown(trial);

  /**
   * Whether the details view is a trial rather than a subscription.
   *
   * A trial is the *other* way the product is open: during one the backend
   * answers `subscription: null` with `access_source: "FREE_TRIAL"`, so a screen
   * that only asked about the subscription showed somebody with fourteen days of
   * complete access the price list and nothing else — no sign of what they were
   * already on, and no sign of how long they had it for.
   *
   * `subscribed` wins where both are somehow true. Somebody who bought a plan
   * during their trial has a subscription to administer, and its invoices and
   * its way out are what this screen is for.
   */
  const trialing = !subscribed && !!countdown;

  /** Whether there is anything to put on the details view at all. */
  const hasPlan = subscribed || trialing;

  const [view, setView] = useState<BillingView>("plans");
  // A subscriber lands on their own plan rather than on the price list — and so
  // does a workspace on its trial. Seeded during the render that first sees it
  // rather than in an effect, so the plans view is never painted and then
  // swapped out from under somebody — the same pattern as the checkout's form.
  const [seededView, setSeededView] = useState(false);
  if (hasPlan && !seededView) {
    setSeededView(true);
    setView("details");
  }

  // A workspace with no plan has nothing to put on the details view, so the
  // switch is not drawn and the price list is the whole screen.
  const showing: BillingView = hasPlan ? view : "plans";

  const invoices = history.invoices;

  /** What to call the plan in the dialogs below, which sit outside the branch
   *  that knows there is one. */
  const named = planName(subscription?.cycle ?? "monthly");

  /**
   * Whether there is a plan here to stop.
   *
   * Everything still running, however badly — a failed renewal is a plan
   * somebody is still being charged for, and the way out of one is not taken
   * away because the payment needs mending. Nothing that is already over, and
   * nothing with a refund against it: those two are what `RefundProgressBanner`
   * and `RefundedPlanCard` replace this whole screen with, and a subscription
   * on its way back to the buyer is not one to cancel.
   *
   * And nothing already called off, which is the case the status alone cannot
   * see. A cancellation leaves the record `ACTIVE` until the cycle it is paid
   * up to runs out — `cancel_requested_at` is the whole of what changes — so a
   * card drawn on the status would sit there offering to cancel a plan that has
   * been cancelled, and take the request. `cancelledOn` is what answers this;
   * see `renews` on `SubscriptionStatusCard`, which reads the same field to head
   * that date "Access until" rather than "Next billing date".
   */
  const cancellable =
    !!subscription &&
    !refunded &&
    !subscription.cancelledOn &&
    !["CANCELLED", "COMPLETED", "EXPIRED"].includes(subscription.status);

  /**
   * A refund in flight settles on its own, so the screen has to go and look.
   *
   * The gateway answers our backend, not this tab — nothing pushes here, so the
   * only way the banner ever becomes the ended-plan card is by asking again.
   * Two minutes: a refund is not something anybody sits and watches, and a pair
   * of requests every five seconds is a tab left open on this screen hammering
   * two endpoints all afternoon for news that arrives once. It stops the moment
   * the record comes back `PROCESSED`, because that is when this condition
   * stops being true.
   *
   * Both are re-read, not just the refund. The subscription ends when the money
   * goes back, and `RefundedPlanCard` dates that from the record — so a poll
   * that refreshed only the refund would swap the banner for a card whose "plan
   * ended" line is still reading the live subscription behind it.
   */
  const settling = refunded?.status === "PENDING";
  const reloadRefund = refundOffer.reload;
  useEffect(() => {
    if (!settling) return;

    const timer = setInterval(() => {
      reloadRefund();
      reload();
    }, REFUND_POLL_MS);
    return () => clearInterval(timer);
  }, [settling, reload, reloadRefund]);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await cancel();
      setConfirmingCancel(false);
      toast.success(
        "Your plan will not renew",
        "You keep everything until the end of the cycle you have paid for."
      );
    } catch (cause) {
      toast.error(
        "Could not cancel your plan",
        cause instanceof Error ? cause.message : "Please try again."
      );
      console.error("subscription cancel failed:", cause);
    } finally {
      setCancelling(false);
    }
  };

  /**
   * Fetching one invoice's PDF.
   *
   * The bytes come from the backend and are handed straight to the browser —
   * nothing is built here, because the document with the invoice number and
   * the registered details on it is the backend's, and a second one drawn in
   * the browser would be a different record of the same payment.
   */
  const handleDownload = async (invoice: SubscriptionInvoice) => {
    setDownloading(invoice.id);
    try {
      const pdf = await fetchInvoiceDocument(getToken, invoice.id);
      saveInvoicePdf(pdf, `naurist-${invoice.number}.pdf`);
    } catch (cause) {
      toast.error(
        "Could not download that invoice",
        cause instanceof Error ? cause.message : "Please try again."
      );
      console.error("invoice download failed:", cause);
    } finally {
      setDownloading(null);
    }
  };

  /**
   * Buying one: on to the checkout for that cycle.
   *
   * The plan travels in the URL rather than in router state, so the page it
   * opens can be reloaded, linked to and gone back to — see `billingCheckout`.
   * Which means this hands over nothing but the cycle, and the checkout looks
   * up the plan itself from the same list this screen drew.
   */
  const handleSubscribe = (plan: SubscriptionPlan) => {
    navigate(billingCheckoutPath(plan.id));
  };

  return (
    // Full height rather than as tall as its cards, so the card below — the
    // way out, or the trial's upgrade — can sit at the foot of the screen on a
    // page whose content does not reach it.
    //
    // `flex-1` and not `min-h-full`: this is a flex item of the Settings
    // screen's tab panel now, and a percentage min-height against a parent
    // that is itself sized by flex resolves to nothing — which left the bar
    // tucked under the history with the rest of the page empty below it. It
    // grows into the panel instead, and the panel takes its own height from
    // `SettingsPage` and in turn from `AppLayout`'s scroll region.
    <div className="flex flex-1 flex-col">
      {/* Only where there are two screens to move between — see the note on
          this component. Drawn as pills rather than tabs so it cannot be read
          as a second row of the Settings tab bar above it. */}
      {hasPlan && (
        <div
          role="group"
          aria-label="Billing views"
          className="flex flex-shrink-0 items-center gap-1 self-start rounded-full border border-line bg-white p-1"
        >
          {VIEWS.map((item) => (
            <button
              key={item.id}
              type="button"
              aria-pressed={showing === item.id}
              onClick={() => setView(item.id)}
              className={`rounded-full px-3.5 py-1.5 text-[13px] 3xl:text-[14px] transition-colors ${
                showing === item.id
                  ? "bg-[#BFF4FB] font-medium text-[#007E8C]"
                  : "text-ink-muted hover:text-ink"
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}

      {showing === "details" && (subscription || countdown) ? (
        <div className="mt-5 3xl:mt-6 flex flex-1 flex-col gap-5 3xl:gap-6">
          {/* One card, four states. A trial first, because during one there is
              no subscription at all to draw the other three from — see
              `TrialPlanCard`. Then the plan, the money on its way back, and the
              plan that has ended because of it, in the order a refund goes
              through them; the `request` on the refund offer is what says which
              — see `useRefundOffer`. */}
          {!subscription ? (
            countdown && (
              <TrialPlanCard
                productName={PRODUCT_NAME}
                countdown={countdown}
              />
            )
          ) : refunded?.status === "PENDING" ? (
            <RefundProgressBanner
              amount={refunded.amount}
              invoiceNumber={refunded.invoiceNumber}
            />
          ) : refunded?.status === "PROCESSED" ? (
            <RefundedPlanCard
              productName={planName(subscription.cycle).productName}
              endedOn={refunded.processedOn ?? subscription.cancelledOn}
              // Coming back is the checkout again, on the cycle they were on.
              onReactivate={() =>
                navigate(billingCheckoutPath(subscription.cycle))
              }
            />
          ) : (
            <SubscriptionStatusCard
              subscription={subscription}
              productName={planName(subscription.cycle).productName}
              cycleLabel={planName(subscription.cycle).cycleLabel}
              // Re-signing a mandate is the checkout again, on the cycle
              // already being paid for: the plan has not changed, only the way
              // it is charged.
              onFixMandate={() =>
                navigate(billingCheckoutPath(subscription.cycle))
              }
            />
          )}

          <BillingHistoryCard
            invoices={invoices}
            phase={historyPhase}
            error={history.error}
            onDownload={handleDownload}
            downloading={downloading}
            // A trial has bought nothing, so an empty table is the first-run
            // state rather than a first charge that has not settled yet — see
            // `purchased` on the card.
            purchased={!!subscription}
          />

          {/* Last on the screen: the one thing that can still be done about
              the payment.

              One card and not two any more. The money back used to take this
              place for the first seven days — `RefundRequestCard`, drawn off
              `GET /billing/refund/`'s `eligible` — and cancelling only took
              over once the window had shut. Asking for a refund is not
              something this screen offers now, so the way out is the way out
              from the day the plan is bought: a subscriber finds the same card
              in the same place whether they are on their second day or their
              second year, rather than one that changes into another one
              overnight.

              What became of a refund already on the record is still reported —
              see `RefundProgressBanner` and `RefundedPlanCard` above, which
              replace the plan card outright. Those are the server's news about
              money that is already moving, not an offer being made here.

              `mt-auto` drops it to the foot of the page rather than leaving it
              tucked under the history: the distance is the point, since the
              way out should not be the next thing read after what you are
              paying for. It is not pinned there — cancelling is not an offer,
              and a card that follows you down the page while you read your
              invoices is pressing something nobody asked for. */}
          {/* A trial has nothing to cancel — the fortnight ends on its own — so
              the one action left is the one that keeps the workspace open past
              it, and it takes the same place at the foot of the screen. This
              one *is* pinned, and that is the difference: it is an offer rather
              than a way out, and it should not have to be scrolled to past a
              history.

              The tint is the page's own fill and is load-bearing: it is what
              the history passes behind rather than through. `pb` gives the
              card air above the window's edge while it is pinned, and the
              matching negative margin takes that space back out of the column
              so its resting position is exactly where it was. */}
          {!subscription ? (
            <div className="mt-auto sticky bottom-0 z-10 -mb-5 bg-[#FCFCFD] pb-5 pt-3 3xl:-mb-8 3xl:pb-8 3xl:pt-4">
              <TrialUpgradeCard
                productName={PRODUCT_NAME}
                // The price list, where the cycle is actually chosen — this
                // card names neither, and picking one on their behalf is not
                // the button's to do.
                onUpgrade={() => setView("plans")}
              />
            </div>
          ) : cancellable ? (
            <div className="mt-auto">
              <CancelPlanCard
                paidUntil={subscription.currentEnd}
                onCancel={() => setConfirmingCancel(true)}
              />
            </div>
          ) : null}
        </div>
      ) : (
        <section aria-labelledby="choose-your-plan" className="mt-5 3xl:mt-6">
          <h2
            id="choose-your-plan"
            className="text-[15px] 3xl:text-[17px] text-ink-muted"
          >
            {/* A workspace whose plan has run out is coming back rather than
                arriving, and the heading should not pretend it has never been
                here. */}
            {subscription ? "Start your plan again" : "Choose your plan"}
          </h2>

          {/* Said as soon as the request goes out, whether or not anything is
              drawn for it: the blocks that go up behind it are the shape of an
              answer rather than the answer. */}
          {priceList.status === "loading" && (
            <p role="status" className="sr-only">
              Loading the plans…
            </p>
          )}

          {priceList.status === "failed" ? (
            // No cards and no prices — and nothing invented in their place. A
            // price list is the one thing on this screen that must not be drawn
            // from anything but the answer the server gave.
            <EmptyState
              className="mt-2"
              {...failedEmptyState(
                "We could not load the plans",
                <Button size="sm" variant="secondary" onClick={priceList.reload}>
                  Try again
                </Button>
              )}
            />
          ) : (
            <>
              {/* Side by side from `lg` up, where two cards of this much text
                  still read; stacked below it, cheaper cycle first, so the
                  recommended one is what the page ends on either way. */}
              <div className="mt-4 3xl:mt-5 grid grid-cols-1 gap-5 lg:grid-cols-2 3xl:gap-6">
                {priceList.status === "ready" ? (
                  priceList.plans.map((plan) => (
                    <PlanCard
                      key={plan.id}
                      plan={plan}
                      onSubscribe={handleSubscribe}
                      active={plan.id === activeCycle}
                    />
                  ))
                ) : (
                  <PlansSkeleton phase={plansPhase} />
                )}
              </div>
            </>
          )}
        </section>
      )}

      <CancelSubscriptionModal
        open={confirmingCancel}
        planName={`${named.productName} - ${named.cycleLabel}`}
        paidUntil={subscription?.currentEnd ?? null}
        cancelling={cancelling}
        onClose={() => setConfirmingCancel(false)}
        onConfirm={handleCancel}
      />
    </div>
  );
};

export default BillingSection;
